// ---------------------------------------------------------------------------
// OpenPencil .fig import — typed failure reasons (UX audit, 2026-07-27)
//
// The creator journey "start from your own data" failed silently: the raw
// upstream body ("Not found") or a bare HTTP status was rendered as the whole
// explanation, and the only guidance was circular ("Review the evidence" —
// the evidence step is what failed).
//
// This module is the ONLY place that turns transport detail into a creator
// reason. It is pure and total: every input maps into the closed
// `OpenPencilImportFailureReason` set, with `unknown` as the honest fallback.
// Rendering code must never print a raw body or status; it renders the copy
// keyed by the reason (openpencil-import-failure-copy.ts).
//
// Signal sources, in priority order:
//   1. Typed codes returned by our own Pages Functions —
//      functions/api/openpencil/figma/[[path]].ts and
//      functions/api/openpencil/workspace-authorization.ts.
//   2. The HTTP status of the failing leg (the import client attaches it).
//   3. Client-side preconditions and abort/timeout signals raised inside
//      openpencil-figma-client.ts.
//
// NOT covered by design: the upstream cogo-openpencil-import-api Worker
// answers 4xx with free English prose (`new HttpError(409, "Import job is
// …")`), which we deliberately do NOT pattern-match — prose is not a
// contract. Those land on the status branch when a status is available and on
// `unknown` otherwise, which is the honest answer.
// ---------------------------------------------------------------------------

export const OPENPENCIL_IMPORT_FAILURE_REASONS = [
  /** The creator picked a file this pipeline cannot read (client-side gate). */
  "unsupported-file-type",
  /** The picked file has no bytes. */
  "empty-source-file",
  /** Studio has no signed-in session to import with. */
  "signed-out",
  /** Signed in, but this source/workspace is not theirs. */
  "not-permitted",
  /** The import service is not wired up in this environment. */
  "service-not-configured",
  /** The import service answered, but could not do the work. */
  "service-unavailable",
  /** The import service is rate-limiting. */
  "service-busy",
  /** The import took longer than the client or proxy allows. */
  "timed-out",
  /** The source is larger than the upload path accepts. */
  "source-too-large",
  /** The referenced import/source no longer exists. */
  "source-not-found",
  /**
   * The import job exists, but its bytes never reached the service — the
   * upload leg itself failed. Distinct from `source-not-found`: there IS a
   * job, so telling the creator their import no longer exists is false.
   */
  "source-upload-failed",
  /** The service accepted the request but refused this source. */
  "source-rejected",
  /** The job is in a state that cannot accept this step. */
  "import-conflict",
  /** The loaded Studio bundle predates the production import wiring. */
  "stale-studio-build",
  /** The creator (or a navigation) stopped the import. */
  "cancelled",
  /** Everything else — presented generically, never as raw transport text. */
  "unknown",
] as const;

export type OpenPencilImportFailureReason =
  (typeof OPENPENCIL_IMPORT_FAILURE_REASONS)[number];

export interface OpenPencilImportFailureSignal {
  /** Typed code from our Pages Function contract, when present. */
  code?: string | null;
  /** HTTP status of the failing leg, when the client captured one. */
  status?: number | null;
  /** Error message — inspected only for OUR OWN typed markers, never prose. */
  message?: string | null;
}

/** Typed codes emitted by the Studio-owned Pages Functions. */
const CODE_REASONS: Record<string, OpenPencilImportFailureReason> = {
  OPENPENCIL_IMPORT_NOT_CONFIGURED: "service-not-configured",
  OPENPENCIL_AUTH_NOT_CONFIGURED: "service-not-configured",
  WORKSPACE_AUTH_NOT_CONFIGURED: "service-not-configured",
  OPENPENCIL_UPSTREAM_FAILED: "service-unavailable",
  OPENPENCIL_UPSTREAM_UNAVAILABLE: "service-unavailable",
  OPENPENCIL_PROXY_UNAVAILABLE: "service-unavailable",
  WORKSPACE_AUTH_UPSTREAM_UNAVAILABLE: "service-unavailable",
  OPENPENCIL_UPSTREAM_TIMEOUT: "timed-out",
  OPENPENCIL_SOURCE_TOO_LARGE_FOR_PROXY: "source-too-large",
  OPENPENCIL_SOURCE_UPLOAD_FAILED: "source-upload-failed",
  OPENPENCIL_LEGACY_ADAPTER_GONE: "stale-studio-build",
  REQUEST_BODY_TOO_LARGE: "source-too-large",
  request_body_too_large: "source-too-large",
  invalid_request: "source-rejected",
  unauthorized: "signed-out",
};

function reasonForStatus(
  status: number,
): OpenPencilImportFailureReason | null {
  if (status === 401) return "signed-out";
  if (status === 403) return "not-permitted";
  if (status === 404) return "source-not-found";
  if (status === 409) return "import-conflict";
  if (status === 413) return "source-too-large";
  if (status === 429) return "service-busy";
  if (status === 504) return "timed-out";
  if (status >= 500) return "service-unavailable";
  if (status >= 400) return "source-rejected";
  return null;
}

function reasonForOwnMessage(
  message: string,
): OpenPencilImportFailureReason | null {
  // Only markers this repo produces — see openpencil-figma-client.ts.
  if (/^sign in before/i.test(message)) return "signed-out";
  if (/\bwas (cancelled|aborted)\b/i.test(message)) return "cancelled";
  if (/\btimed out\b/i.test(message)) return "timed-out";
  if (/\bis not configured\b/i.test(message)) return "service-not-configured";
  return null;
}

const CODE_TOKEN_RE = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/;
const OWN_STATUS_TEMPLATE_RE = /\breturned (\d{3})\b/;

/**
 * Total classifier. Priority: typed code → typed code embedded in the
 * message → our own client markers → transport status → unknown.
 */
export function classifyOpenPencilImportFailure(
  signal: OpenPencilImportFailureSignal,
): OpenPencilImportFailureReason {
  const code = signal.code?.trim();
  if (code && CODE_REASONS[code]) return CODE_REASONS[code];

  const message = signal.message?.trim() ?? "";
  if (message) {
    const embedded = message.match(CODE_TOKEN_RE)?.[0];
    if (embedded && CODE_REASONS[embedded]) return CODE_REASONS[embedded];
    const own = reasonForOwnMessage(message);
    if (own) return own;
  }

  if (typeof signal.status === "number" && Number.isFinite(signal.status)) {
    const byStatus = reasonForStatus(signal.status);
    if (byStatus) return byStatus;
  }

  if (message) {
    const templated = message.match(OWN_STATUS_TEMPLATE_RE)?.[1];
    if (templated) {
      const byTemplate = reasonForStatus(Number(templated));
      if (byTemplate) return byTemplate;
    }
  }

  return "unknown";
}

/**
 * Classify a stored blocked-bundle reason (`bundle.blockedEvidence.reason`).
 * Used when a workspace is restored from storage and the live failure signal
 * is gone — the text still carries either our typed code or one of our own
 * "… returned <status>." templates.
 */
export function classifyOpenPencilImportFailureText(
  reason: string | null | undefined,
): OpenPencilImportFailureReason {
  return classifyOpenPencilImportFailure({ message: reason ?? "" });
}
